// The Service Catalog list shows the start of each response. Its height is capped by the list itself
// (ClampedPreview), which measures what the browser drew - so the cut here is only about characters, to keep a very
// long response from being drawn in full.
//
// The character limit counts what the list shows, not the Markdown behind it. Counting the source let a response
// pasted from a web page show three lines: a link's address and a picture's address are often a hundred characters
// each and show nothing. And a cut must not land inside markup, or the rest shows raw - `**Week ...`.

export const LIST_PREVIEW_CHARS = 350;

const FENCE = /^\s*(```|~~~)/;

/** Pictures, links and the spacing-line entity: markup whose source length is nothing like what it shows. */
const TOKEN = /!\[[^\]]*\]\([^)]*\)|\[([^\]]*)\]\([^)]*\)|&nbsp;/g;

/**
 * Where `maxChars` characters of what the list shows run out, as an offset into the source - or null if they never
 * do. A picture shows no characters (the list's height cap has its height), a link shows its text, `&nbsp;` one space.
 * Never inside a picture or a link: one the budget runs out in is kept whole.
 */
export const visibleCut = (markdown: string, maxChars: number): number | null => {
  let visible = 0;
  let at = 0;
  for (const token of markdown.matchAll(TOKEN)) {
    const plain = token.index - at;
    if (visible + plain > maxChars) return at + (maxChars - visible);
    visible += plain;
    at = token.index + token[0].length;
    if (!token[0].startsWith('!'))
      visible += token[1] === undefined ? 1 : token[1].length;
    if (visible > maxChars) return at;
  }
  return visible + (markdown.length - at) > maxChars ? at + (maxChars - visible) : null;
};

/** The cut carried on to the end of its word, as before - but stopping short of a picture or link that follows. */
const toWordEnd = (markdown: string, cut: number): number => {
  const space = markdown.slice(cut).search(/\s/);
  const wordEnd = space === -1 ? markdown.length : cut + space;
  const next = [...markdown.matchAll(TOKEN)].find(t => t.index >= cut && t.index < wordEnd);
  return next && next.index + next[0].length > wordEnd ? next.index : wordEnd;
};

/**
 * Closes whatever the cut left open, so the rest of the list does not render as code or bold: a fenced block, then
 * inline code, then `**` and `~~`, latest-opened first.
 */
export const closeOpenMarks = (preview: string): string => {
  if (preview.split('\n').filter(line => FENCE.test(line)).length % 2 === 1) return `${preview}\n\`\`\``;
  const outsideFences = preview.split(/^\s*(?:```|~~~).*$/m).filter((_, i) => i % 2 === 0).join('\n');
  if ((outsideFences.match(/`/g) ?? []).length % 2 === 1) return `${preview}\``;
  const prose = outsideFences.replace(/`[^`]*`/g, '');
  const open = ['**', '~~']
    .filter(mark => prose.split(mark).length % 2 === 0)
    .sort((a, b) => prose.lastIndexOf(b) - prose.lastIndexOf(a));
  return preview + open.join('');
};

/**
 * The response as the list shows it, cut once the character limit is reached, and whether it was cut. No "..." is
 * added to the text: the list puts one on a line of its own, the same for this cut as for its height cap.
 */
export const listPreview = (markdown: string, maxChars = LIST_PREVIEW_CHARS): { text: string; cut: boolean } => {
  // Covers null and undefined as well as empty, which `.length` alone would throw on.
  if (!markdown?.length) return { text: markdown, cut: false };
  const text = markdown.replace(/\r\n/g, '\n');
  const byChars = visibleCut(text, maxChars);
  if (byChars === null) return { text, cut: false };
  const end = toWordEnd(text, byChars);
  if (end >= text.length) return { text, cut: false };
  return { text: closeOpenMarks(text.slice(0, end).replace(/\s+$/, '')), cut: true };
};
